import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { trackAppEvent } from "../lib/appTelemetry";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const AuthContext = createContext(null);

// Token helpers — primary auth via localStorage Bearer token (cookies are fallback)
const TOKEN_KEY = "astrofate_access_token";
const REFRESH_KEY = "astrofate_refresh_token";
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const getRefreshToken = () => localStorage.getItem(REFRESH_KEY);
export const setTokens = (access, refresh) => {
  if (access) localStorage.setItem(TOKEN_KEY, access);
  if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
};
export const clearTokens = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
};

// Auto-attach the Bearer token on every axios request
axios.interceptors.request.use((config) => {
  const t = getToken();
  if (t && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

// Keep the native Android shell in sync with the freshest tokens
const syncNativeSession = (access, refresh) => {
  try { window.AstroFateNative?.saveSession?.(access || "", refresh || ""); } catch { /* web */ }
};

// ── Silent refresh ──────────────────────────────────────────────────────────
// The access token lives 15 minutes. Without this, the very next request after
// that returned 401 and the app looked logged out. Now a 401 transparently
// refreshes (single-flight, so parallel 401s trigger exactly one refresh) and
// the original request is retried. Sessions only end on explicit logout.
let refreshPromise = null;

export const runTokenRefresh = () => {
  if (!refreshPromise) {
    const stored = getRefreshToken();
    refreshPromise = axios
      .post(`${API}/auth/refresh`, stored ? { refresh_token: stored } : {}, {
        withCredentials: true,
        headers: { Authorization: undefined },
        _skipAuthRefresh: true,
      })
      .then(res => {
        const { access_token, refresh_token } = res.data || {};
        if (access_token) setTokens(access_token, refresh_token);
        syncNativeSession(access_token || getToken(), refresh_token || getRefreshToken());
        return access_token || null;
      })
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
};

axios.interceptors.response.use(
  (res) => res,
  async (error) => {
    const cfg = error.config || {};
    const status = error.response?.status;
    const isAuthCall = typeof cfg.url === "string" && cfg.url.includes("/auth/refresh");
    if (status !== 401 || cfg._retried || cfg._skipAuthRefresh || isAuthCall) {
      return Promise.reject(error);
    }
    try {
      const fresh = await runTokenRefresh();
      if (!fresh) return Promise.reject(error);
      cfg._retried = true;
      cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${fresh}` };
      return axios(cfg);
    } catch {
      return Promise.reject(error);
    }
  }
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading, null = not auth

  useEffect(() => {
    // Returning from Google OAuth — AuthCallback exchanges the session_id first
    if (window.location.hash?.includes("session_id=")) return;
    // The response interceptor already retries a 401 once via /auth/refresh, so
    // reaching the catch here means there is genuinely no usable session left.
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(res => setUser(res.data))
      .catch(() => { clearTokens(); setUser(null); });
  }, []);

  const login = async (identifier, password) => {
    const res = await axios.post(`${API}/auth/login`, { identifier, email: identifier, password }, { withCredentials: true });
    setTokens(res.data.access_token, res.data.refresh_token);
    syncNativeSession(res.data.access_token, res.data.refresh_token);
    setUser(res.data);
    trackAppEvent("login", "auth");
    return res.data;
  };

  const register = async (name, email, password, phone = "") => {
    const res = await axios.post(`${API}/auth/register`, { name, email, password, phone }, { withCredentials: true });
    setTokens(res.data.access_token, res.data.refresh_token);
    syncNativeSession(res.data.access_token, res.data.refresh_token);
    setUser(res.data);
    trackAppEvent("signup", "auth");
    return res.data;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch {}
    clearTokens();
    // Nothing personal may survive a logout on a shared phone — the cached
    // Kundli/chart is wiped so the next visitor starts from a clean slate.
    try { localStorage.removeItem("astrofate_report"); } catch { /* private mode */ }
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(res.data);
      return res.data;
    } catch {
      setUser(null);
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, register, login, logout, refreshUser, loading: user === undefined }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function formatError(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}
